import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { conversationsAPI } from '../services/api'
import {
  MessageSquare, Send, User, Clock, Search,
  ArrowLeft, Bell, Check, CheckCheck, AlertCircle,
  Loader, Calendar, Euro, Briefcase, Trash2
} from 'lucide-react'

// embedded: gerenderd in de dashboard-shell (#dashboard-scroll) — dan vult de pagina de container
// i.p.v. de viewport, zodat de berichtenlijst intern scrolt en de inputbalk zichtbaar blijft.
const MessagesPage = ({ embedded = false }) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [conversations, setConversations] = useState([])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalUnread, setTotalUnread] = useState(0)
  const [mobileShowChat, setMobileShowChat] = useState(false)
  const messagesEndRef = useRef(null)
  const pollIntervalRef = useRef(null)

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    // block:'end' + nearest: alleen de berichtenlijst scrollen, niet de dashboard-container eromheen
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Fetch conversations on mount
  useEffect(() => {
    fetchConversations()

    // Set up polling for new messages every 5 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchConversations(true) // Silent refresh
      if (selectedConversation) {
        refreshCurrentConversation()
      }
    }, 5000)

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [])

  const fetchConversations = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const response = await conversationsAPI.getConversations({ limit: 50 })
      const convs = response.data.conversations || []
      setConversations(convs)

      // Calculate total unread
      const unread = convs.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0)
      setTotalUnread(unread)

      // Update page title with unread count
      if (unread > 0) {
        document.title = `(${unread}) Berichten - UGC4you`
      } else {
        document.title = 'Berichten - UGC4you'
      }

      // Auto-select conversation from URL query parameter
      if (!silent && !selectedConversation) {
        const conversationId = searchParams.get('conversation')
        const projectParam = searchParams.get('project')
        if (conversationId && convs.length > 0) {
          const targetConv = convs.find(c => c.id === conversationId || c._id === conversationId)
          if (targetConv) {
            selectConversation(targetConv)
          }
          // Clean up the URL
          searchParams.delete('conversation')
          setSearchParams(searchParams, { replace: true })
        } else if (projectParam && convs.length > 0) {
          // "Chat"-knop vanaf Mijn projecten (?project=<id>): gesprek van dat project openen
          const targetConv = convs.find(c => c.project?.id === projectParam || c.project?._id === projectParam)
          if (targetConv) {
            selectConversation(targetConv)
          }
          searchParams.delete('project')
          setSearchParams(searchParams, { replace: true })
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error)
      if (!silent) setError('Kon gesprekken niet laden')
    } finally {
      if (!silent) setLoading(false)
    }
  }

  const refreshCurrentConversation = async () => {
    if (!selectedConversation) return

    try {
      const response = await conversationsAPI.getConversation(selectedConversation.id)
      const conv = response.data.conversation
      if (conv) {
        setMessages(conv.messages || [])
        // Update the conversation in the list
        setConversations(prev => prev.map(c =>
          c.id === conv.id ? { ...c, unreadCount: 0 } : c
        ))
      }
    } catch (error) {
      console.error('Error refreshing conversation:', error)
    }
  }

  const selectConversation = async (conversation) => {
    try {
      setSelectedConversation(conversation)
      setMessages([])
      setError('')
      setMobileShowChat(true)

      const response = await conversationsAPI.getConversation(conversation.id)
      const conv = response.data.conversation

      if (conv) {
        setMessages(conv.messages || [])
        setSelectedConversation(conv)

        // Mark as read - update local state
        setConversations(prev => prev.map(c =>
          c.id === conversation.id ? { ...c, unreadCount: 0 } : c
        ))

        // Recalculate total unread
        const unread = conversations.reduce((sum, c) =>
          c.id === conversation.id ? sum : sum + (c.unreadCount || 0), 0
        )
        setTotalUnread(unread)
      }
    } catch (error) {
      console.error('Error loading conversation:', error)
      setError('Kon gesprek niet laden')
    }
  }

  const handleDeleteConversation = async (conversation, e) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    const name = conversation.participant?.name || 'deze deelnemer'
    if (!window.confirm(`Weet je zeker dat je het gesprek met ${name} wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`)) return
    try {
      await conversationsAPI.deleteConversation(conversation.id)
      // Verwijder uit lokale state
      setConversations(prev => prev.filter(c => c.id !== conversation.id))
      if (selectedConversation?.id === conversation.id) {
        setSelectedConversation(null)
        setMessages([])
        setMobileShowChat(false)
      }
    } catch (error) {
      console.error('Error deleting conversation:', error)
      const msg = error?.response?.data?.message || 'Kon gesprek niet verwijderen'
      alert(msg)
    }
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || !selectedConversation || sending) return

    try {
      setSending(true)
      const messageContent = newMessage.trim()
      setNewMessage('')

      const response = await conversationsAPI.sendMessage(selectedConversation.id, {
        content: messageContent
      })

      // Add message to local state
      setMessages(prev => [...prev, response.data.message])

      // Update last message in conversation list
      setConversations(prev => prev.map(conv =>
        conv.id === selectedConversation.id
          ? {
              ...conv,
              lastMessage: {
                content: messageContent,
                sentAt: new Date().toISOString(),
                isFromMe: true
              }
            }
          : conv
      ))
    } catch (error) {
      console.error('Error sending message:', error)
      setError('Bericht kon niet worden verzonden')
      setNewMessage(messageContent) // Restore message on error
    } finally {
      setSending(false)
    }
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now - date) / (1000 * 60 * 60)

    if (diffInHours < 1) {
      return 'Zojuist'
    } else if (diffInHours < 24) {
      return date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
    } else if (diffInHours < 48) {
      return 'Gisteren'
    } else {
      return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
    }
  }

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true
    const participantName = conv.participant?.name?.toLowerCase() || ''
    const projectTitle = conv.project?.title?.toLowerCase() || ''
    const query = searchQuery.toLowerCase()
    return participantName.includes(query) || projectTitle.includes(query)
  })

  if (loading && conversations.length === 0) {
    return (
      <div className={`font-['Inter'] ${embedded ? 'h-full' : 'min-h-screen'} flex items-center justify-center bg-white text-[#0F172A]`}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#3EB489] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#475569]">Berichten laden...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`font-['Inter'] ${embedded ? 'h-full' : 'h-screen supports-[height:100dvh]:h-[100dvh]'} flex flex-col overflow-hidden bg-white text-[#0F172A]`}>
      {/* Header */}
      <header className="w-full shrink-0 flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-gray-100 sticky top-0 z-50 bg-white/95 backdrop-blur-xl">
        <div className="flex items-center gap-4 md:gap-6">
          <Link to="/" className="text-xl md:text-2xl font-black tracking-tighter text-[#0F172A]">
            UGC<span className="text-[#3EB489]">4you</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-sm hover:text-[#3EB489] transition">Dashboard</Link>
            <Link to="/messages" className="text-sm text-[#3EB489] flex items-center gap-2">
              Berichten
              {totalUnread > 0 && (
                <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {totalUnread}
                </span>
              )}
            </Link>
            <Link to="/projects" className="text-sm hover:text-[#3EB489] transition">Opdrachten</Link>
          </nav>
        </div>

        <div className="flex items-center gap-2">
          {/* Mobile nav links */}
          <nav className="flex md:hidden items-center gap-1 mr-1">
            <Link to="/dashboard" className="inline-flex items-center min-h-[40px] px-2 py-2 text-sm hover:text-[#3EB489] transition">Dashboard</Link>
            <Link to="/projects" className="inline-flex items-center min-h-[40px] px-2 py-2 text-sm hover:text-[#3EB489] transition">Opdrachten</Link>
          </nav>
          <Link
            to="/dashboard"
            className="hidden md:flex px-4 py-2 bg-[#F8FAFC] hover:bg-gray-100 rounded-lg font-medium transition items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Terug
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 min-h-0 relative flex overflow-hidden">
        <div className="max-w-7xl w-full mx-auto flex min-h-0">
          {/* Conversations List */}
          <div className={`w-full md:w-96 min-h-0 border-r border-gray-100 flex flex-col ${mobileShowChat ? 'hidden md:flex' : 'flex'}`}>
            {/* Search */}
            <div className="p-4 border-b border-gray-100 shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]/60" />
                <input
                  type="text"
                  placeholder="Zoek gesprekken..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:border-[#3EB489] focus:outline-none transition text-base text-[#0F172A] placeholder-[#475569]/50"
                />
              </div>
            </div>

            {/* Conversations */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-hide">
              {filteredConversations.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-[#475569]">Geen gesprekken</p>
                  <p className="text-sm text-[#475569]/60 mt-2">
                    {searchQuery ? 'Geen resultaten gevonden' : 'Start een gesprek via een opdracht'}
                  </p>
                </div>
              ) : (
                filteredConversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectConversation(conversation)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectConversation(conversation) } }}
                    className={`group relative w-full p-4 hover:bg-[#F8FAFC] transition text-left border-b border-gray-50 cursor-pointer ${
                      selectedConversation?.id === conversation.id ? 'bg-gray-100' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        {conversation.participant?.profilePictureUrl ? (
                          <img
                            src={conversation.participant.profilePictureUrl}
                            alt={conversation.participant.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#3EB489] flex items-center justify-center">
                            <User className="w-5 h-5 text-white" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pr-9">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold text-[#0F172A] truncate pr-2">
                            {conversation.participant?.name || 'Onbekend'}
                          </h3>
                          <span className="text-xs text-[#475569]/60 flex-shrink-0">
                            {conversation.lastMessage && formatTime(conversation.lastMessage.sentAt)}
                          </span>
                        </div>

                        {conversation.project && (
                          <div className="flex items-center gap-1 text-xs text-[#3EB489] mb-1">
                            <Briefcase className="w-3 h-3" />
                            <span className="truncate">{conversation.project.title}</span>
                          </div>
                        )}

                        <p className="text-sm text-[#475569] truncate">
                          {conversation.lastMessage?.content || 'Geen berichten'}
                        </p>

                        {/* Unread badge */}
                        {conversation.unreadCount > 0 && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="px-2 py-0.5 bg-[#3EB489] text-white text-xs rounded-full">
                              {conversation.unreadCount} nieuw
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Verwijder-knop — zichtbaar op hover (desktop) en altijd op touch */}
                    <button
                      type="button"
                      onClick={(e) => handleDeleteConversation(conversation, e)}
                      aria-label="Gesprek verwijderen"
                      title="Gesprek verwijderen"
                      className="absolute bottom-2 right-2 p-2.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition opacity-0 group-hover:opacity-100 focus:opacity-100 md:opacity-0 max-md:opacity-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 min-w-0 min-h-0 flex flex-col ${mobileShowChat ? 'flex' : 'hidden md:flex'}`}>
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-3 md:p-4 border-b border-gray-100 flex items-center justify-between gap-2 shrink-0">
                  <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    {/* Mobile back button */}
                    <button
                      onClick={() => setMobileShowChat(false)}
                      className="md:hidden flex-shrink-0 p-2.5 -ml-2 hover:bg-gray-100 rounded-lg transition"
                      aria-label="Terug naar gesprekken"
                    >
                      <ArrowLeft className="w-5 h-5 text-[#475569]" />
                    </button>
                    {selectedConversation.participant?.profilePictureUrl ? (
                      <img
                        src={selectedConversation.participant.profilePictureUrl}
                        alt={selectedConversation.participant.name}
                        className="w-9 h-9 md:w-10 md:h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-[#3EB489] flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 md:w-5 md:h-5 text-white" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <h2 className="font-semibold text-[#0F172A] text-sm md:text-base truncate">
                        {selectedConversation.participant?.name || 'Onbekend'}
                      </h2>
                      {selectedConversation.project && (
                        <p className="text-xs text-[#475569] truncate">
                          {selectedConversation.project.title}
                        </p>
                      )}
                    </div>
                  </div>

                  {selectedConversation.project && (
                    <Link
                      to={`/projects/${selectedConversation.project.id}`}
                      className="flex-shrink-0 px-2.5 py-1.5 bg-[#F8FAFC] hover:bg-gray-100 rounded-lg text-xs md:text-sm transition"
                    >
                      <span className="hidden sm:inline">Bekijk project</span>
                      <span className="sm:hidden">Project</span>
                    </Link>
                  )}
                </div>

                {/* Messages */}
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-hide p-3 md:p-6 space-y-3 md:space-y-4">
                  {messages.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <MessageSquare className="w-8 h-8 text-[#3EB489]" />
                      </div>
                      <p className="text-[#475569] mb-2">Nog geen berichten</p>
                      <p className="text-sm text-[#475569]/60">
                        Start het gesprek door een bericht te sturen
                      </p>
                    </div>
                  ) : (
                    <>
                      {messages.map((message, index) => {
                        // Determine if message is from current user
                        const isMyMessage = message.sender.isMe ||
                                          (message.sender.id && user?.id && message.sender.id === user.id)

                        return (
                          <div
                            key={message.id || index}
                            className={`flex gap-2 md:gap-3 ${!isMyMessage ? 'flex-row-reverse' : ''}`}
                          >
                            <div className="flex-shrink-0 hidden sm:block">
                              {message.sender.profilePictureUrl ? (
                                <img
                                  src={message.sender.profilePictureUrl}
                                  alt={message.sender.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-[#3EB489] flex items-center justify-center">
                                  <User className="w-4 h-4 text-white" />
                                </div>
                              )}
                            </div>
                            <div className={`max-w-[85%] md:max-w-md ${!isMyMessage ? 'text-right' : ''}`}>
                              <div
                                className={`inline-block px-3 md:px-4 py-2 rounded-2xl ${
                                  isMyMessage
                                    ? 'bg-[#F8FAFC] text-[#0F172A] border border-gray-100'
                                    : 'bg-[#3EB489] text-white'
                                }`}
                              >
                                <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                              </div>
                              <div className={`flex items-center gap-1 mt-1 text-xs text-[#475569]/60 ${!isMyMessage ? 'justify-end' : ''}`}>
                                <Clock className="w-3 h-3" />
                                <span>{formatTime(message.sentAt)}</span>
                                {isMyMessage && (
                                  message.read ? (
                                    <CheckCheck className="w-3 h-3 text-[#3EB489] ml-1" />
                                  ) : (
                                    <Check className="w-3 h-3 ml-1" />
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input — shrink-0: blijft altijd onderaan in beeld */}
                <form onSubmit={handleSendMessage} className="p-2.5 md:p-4 border-t border-gray-100 shrink-0 bg-white">
                  <div className="flex gap-2 md:gap-3">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type een bericht..."
                      className="flex-1 min-w-0 px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-xl focus:border-[#3EB489] focus:outline-none transition text-base text-[#0F172A] placeholder-[#475569]/50"
                      disabled={sending}
                      maxLength={2000}
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="px-3 md:px-6 py-2.5 md:py-3 bg-[#3EB489] hover:bg-[#3EB489]/90 disabled:bg-gray-100 disabled:text-[#475569]/60 rounded-xl transition flex items-center gap-2 text-white font-medium flex-shrink-0"
                    >
                      {sending ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      <span className="hidden md:inline">Verzend</span>
                    </button>
                  </div>
                  {newMessage.length > 1800 && (
                    <p className="text-xs text-[#475569] mt-1">
                      {2000 - newMessage.length} karakters over
                    </p>
                  )}
                </form>
              </>
            ) : (
              <div className="flex-1 hidden md:flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-10 h-10 text-[#3EB489]" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Selecteer een gesprek</h3>
                  <p className="text-[#475569]">
                    Kies een gesprek uit de lijst om berichten te bekijken
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MessagesPage